import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  Plus, Search, Edit2, Send, Trash2, ShieldAlert, 
  FileText, Calendar, Eye
} from 'lucide-react';
import { FaArrowLeft } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../common/apiClient';
import PageLoader from '../../helpers/PageLoader';
import FormOffCanvas from '../ui/FormOffCanvas';
import OfferForm from './OfferForm';
import OfferDetailView from './OfferDetailView';
import ModernTable from '../ui/ModernTable';
import SelectDropdown from '../../customStyles/SelectDropdown'; // Imported SelectDropdown for filters
import DeleteConfirmationOffcanvas from '../../helpers/DeleteConfirmationOffcanvas'; // Custom confirmation modal
import SummaryApi from '../../common/SummaryApi';

// Status options config for SelectDropdown filter
const statusFilterOptions = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'withdrawn', label: 'Withdrawn' }
];

const OfferList = () => {
  const { portalName } = useParams();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  // URL Parameters for controlling detailed sub-views
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get("offerView") || "list"; // default is 'list'
  const selectedId = searchParams.get("id"); // selected Offer ID to view

  // Filters State
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Form handling states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'

  // Withdraw flow states
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const [withdrawOfferId, setWithdrawOfferId] = useState(null);
  const [withdrawReason, setWithdrawReason] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  // Delete Confirmation States
  const [selectedOfferToDelete, setSelectedOfferToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // 👈 Send/Issue Confirmation States (Using DeleteConfirmationOffcanvas dynamically)
  const [selectedOfferToIssue, setSelectedOfferToIssue] = useState(null);
  const [isIssuing, setIsIssuing] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Fetch all candidate offers
  const fetchOffers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(SummaryApi.getAllCandidateOffers.url);
      if (response.data.success) {
        setOffers(response.data.offers || []);
      }
    } catch (err) {
      toast.error('Failed to load candidate offers list');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOffers();
  }, [fetchOffers]);

  // Set Sub-View state using URL Parameters
  const setView = useCallback((newView) => {
    const params = new URLSearchParams(searchParams);
    if (newView === "list") {
      params.delete("offerView");
      params.delete("id");
    } else {
      params.set("offerView", newView);
      if (!newView.startsWith("view_")) {
        params.delete("id");
      }
    }
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCreateOpen = () => {
    setFormMode('create');
    setSelectedOffer(null);
    setIsFormOpen(true);
  };

  const handleEditOpen = (offer) => {
    if (offer.status !== 'draft') {
      toast.warning('Only offers in draft status can be edited.');
      return;
    }
    setFormMode('edit');
    setSelectedOffer(offer);
    setIsFormOpen(true);
  };

  // View Detailed Offer Page
  const handleView = (row) => {
    const params = new URLSearchParams(searchParams);
    params.set("offerView", "view_offer");
    params.set("id", row._id);
    setSearchParams(params, { replace: true });
  };

  // 👈 Confirm Send/Issue Offer Trigger
  const handleIssueConfirm = async () => {
    if (!selectedOfferToIssue) return;
    setIsIssuing(true);
    try {
      const response = await api.post(SummaryApi.sendCandidateOffer.url(selectedOfferToIssue._id));
      if (response.data.success) {
        toast.success('Offer successfully sent to candidate email!');
        setSelectedOfferToIssue(null);
        fetchOffers();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send offer');
    } finally {
      setIsIssuing(false);
    }
  };

  // Trigger Delete Offer API using custom DeleteConfirmationOffcanvas confirm trigger
  const handleDeleteConfirm = async () => {
    if (!selectedOfferToDelete) return;
    setIsDeleting(true);
    try {
      const response = await api.delete(SummaryApi.deleteCandidateOffer.url(selectedOfferToDelete._id));
      if (response.data.success) {
        toast.success('Offer record deleted successfully');
        setSelectedOfferToDelete(null);
        fetchOffers();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete offer');
    } finally {
      setIsDeleting(false);
    }
  };

  // Trigger Withdraw Offer API
  const handleOpenWithdraw = (id) => {
    setWithdrawOfferId(id);
    setWithdrawReason('');
    setIsWithdrawOpen(true);
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    if (!withdrawReason.trim()) {
      toast.warning('Please specify a reason for withdrawal');
      return;
    }
    setWithdrawing(true);
    try {
      const response = await api.post(SummaryApi.withdrawCandidateOffer.url(withdrawOfferId), {
        reason: withdrawReason
      });
      if (response.data.success) {
        toast.success('Offer successfully withdrawn');
        setIsWithdrawOpen(false);
        fetchOffers();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to withdraw offer');
    } finally {
      setWithdrawing(false);
    }
  };

  // Helper to format currency values
  const formatCurrency = (val) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  // Status Badge styling helper
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-700 border-gray-200';
      case 'sent': return 'bg-blue-50 text-blue-700 border-blue-200';
      case 'accepted': return 'bg-green-50 text-green-700 border-green-200';
      case 'rejected': return 'bg-red-50 text-red-700 border-red-200';
      case 'withdrawn': return 'bg-amber-50 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  // Filter and Search logic
  const filteredOffers = useMemo(() => {
    return offers.filter(item => {
      const candidateName = `${item.candidateId?.firstName || ''} ${item.candidateId?.lastName || ''}`.toLowerCase();
      const offerId = (item.offerId || '').toLowerCase();
      const matchesSearch = candidateName.includes(searchQuery.toLowerCase()) || offerId.includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [offers, searchQuery, statusFilter]);

  const paginatedData = useMemo(() => {
    if (itemsPerPage === 'All') return filteredOffers;
    const start = (currentPage - 1) * Number(itemsPerPage);
    return filteredOffers.slice(start, start + Number(itemsPerPage));
  }, [filteredOffers, currentPage, itemsPerPage]);

  // Columns configuration setup strictly mapped for `<ModernTable />`
  const columns = [
    {
      label: 'Offer ID',
      key: 'offerId',
      render: (row) => <span className="font-bold text-gray-900">{row.offerId || 'PENDING ID'}</span>
    },
    {
      label: 'Candidate',
      key: 'candidate',
      render: (row) => (
        <div className="flex flex-col min-w-[150px]">
          <span className="font-bold text-gray-800 capitalize">{row.candidateId?.firstName} {row.candidateId?.lastName}</span>
          <span className="text-[10px] text-gray-400 mt-0.5">{row.candidateId?.email}</span>
        </div>
      )
    },
    {
      label: 'Job Specifications',
      key: 'jobInfo',
      render: (row) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-700">{row.designationId?.name || row.designationId?.title || 'Assigned Position'}</span>
          <span className="text-[10px] text-gray-400 mt-0.5">{row.departmentId?.name || row.departmentId?.departmentName || 'N/A'}</span>
        </div>
      )
    },
    {
      label: 'Compensation (CTC)',
      key: 'ctc',
      render: (row) => <span className="font-bold text-gray-800">{formatCurrency(row.ctc)}</span>
    },
    {
      label: 'Target Joining',
      key: 'joiningDate',
      render: (row) => (
        <div className="flex items-center gap-1.5 text-gray-600 whitespace-nowrap">
          <Calendar className="h-4 w-4 text-gray-400" />
          <span>{new Date(row.joiningDate).toLocaleDateString('en-IN', { dateStyle: 'medium' })}</span>
        </div>
      )
    },
    {
      label: 'Status',
      key: 'status',
      render: (row) => (
        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase border ${getStatusBadgeClass(row.status)}`}>
          {row.status}
        </span>
      )
    }
  ];

  // 👈 Dynamic Row actions list passed to `<ModernTable />` using standard RowAction model
  const tableActions = [
    {
      label: 'View Detailed Offer',
      icon: <Eye className="h-4 w-4" />,
      onClick: (row) => handleView(row)
    },
    {
      label: 'Edit Offer Package',
      icon: <Edit2 className="h-4 w-4" />,
      onClick: (row) => handleEditOpen(row),
      show: (row) => row.status === 'draft'
    },
    {
      label: 'Issue Offer Letter',
      icon: <Send className="h-4 w-4 text-primary" />,
      onClick: (row) => setSelectedOfferToIssue(row), // Triggers custom confirmation modal
      show: (row) => row.status === 'draft'
    },
    {
      label: 'Withdraw Offer',
      icon: <ShieldAlert className="h-4 w-4 text-amber-600" />,
      onClick: (row) => handleOpenWithdraw(row._id),
      show: (row) => row.status === 'sent',
      danger: true
    },
    {
      label: 'Delete Offer Record',
      icon: <Trash2 className="h-4 w-4 text-red-600" />,
      onClick: (row) => setSelectedOfferToDelete(row), // Triggers custom delete offcanvas
      show: (row) => row.status === 'draft',
      danger: true
    }
  ];

  // Check view conditions to render dedicated Single Offer Details View Page
  if (view === "view_offer") {
    return (
      <OfferDetailView 
        id={selectedId} 
        onBack={() => setView("list")} 
      />
    );
  }

  if (loading) return <PageLoader />;

  return (
   <>
    <div className="space-y-6">
      
      {/* Header Panel */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-4 md:p-6 rounded-xl border border-gray-100 shadow-sm">
        <div className="flex items-center gap-2">
          {/* Back button navigates back to recruitment dashboard */}
          <button 
            onClick={() => navigate(`/${portalName}/hr/recruitment-management?tab=offers`)} 
            className="rounded-lg bg-red-50 border border-red-100 text-textPrimary hover:text-primary p-2 transition-colors"
          >
            <FaArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 leading-tight">Candidate Offer Letters</h1>
            <p className="text-xs text-gray-500 mt-1">Configure, review, issue and manage job offer packages issued to selected candidates.</p>
          </div>
        </div>
        <button 
          onClick={handleCreateOpen}
          className="bg-primary hover:bg-secondary text-white px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors shrink-0"
        >
          <Plus className="h-4 w-4" /> Create Offer Package
        </button>
      </div>

      {/* Filter and Search Panel using SelectDropdown */}
      <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm flex flex-col sm:flex-row gap-4 items-end sm:items-center">
        <div className="relative flex-1 w-full mt-5">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search candidate name or Offer ID..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 outline-none text-sm bg-gray-50/50"
          />
        </div>
        {/* 👈 Updated: Replaced standard HTML select filter with custom SelectDropdown */}
        <div className="w-full sm:w-64">
          <SelectDropdown
            label="Filter by Status"
            options={statusFilterOptions}
            value={statusFilter}
            onChange={setStatusFilter}
            valueKey="value"
            labelKey="label"
            searchable={false}
            zIndex={40}
            multiple={false}
          />
        </div>
      </div>

      {/* Modern Table Representation */}
      {filteredOffers.length > 0 ? (
        <ModernTable
          columns={columns}
          data={paginatedData}
          actions={tableActions} // 👈 Added standard Actions parameter
          selectable={false}
          pagination={{
            totalItems: filteredOffers.length,
            itemsPerPage: itemsPerPage,
            currentPage: currentPage,
            onPageChange: setCurrentPage,
            onItemsPerPageChange: (v) => { setItemsPerPage(v); setCurrentPage(1); }
          }}
        />
      ) : (
        /* Empty block representation */
        <div className="bg-white border rounded-xl p-12 flex flex-col items-center justify-center text-center shadow-sm space-y-4">
          <div className="p-3 bg-red-50 rounded-full text-primary border border-red-100">
            <FileText className="h-8 w-8" />
          </div>
          <div>
            <h3 className="font-bold text-gray-800 text-sm">No Offer Packages Issued</h3>
            <p className="text-xs text-gray-400 mt-1">Create and configure candidate compensation and joining structures to issue offer contracts.</p>
          </div>
          <button 
            onClick={handleCreateOpen}
            className="bg-primary hover:bg-red-700 text-white px-4 py-2 rounded-lg text-xs font-bold"
          >
            Create Offer Package
          </button>
        </div>
      )}

    </div>

      {/* Offer Form Offcanvas */}
      <OfferForm 
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        mode={formMode}
        offer={selectedOffer}
        onSave={fetchOffers}
      />

      {/* Withdraw Offer Offcanvas Panel */}
      <FormOffCanvas 
        isOpen={isWithdrawOpen} 
        onClose={() => setIsWithdrawOpen(false)} 
        title="Withdraw Issue Package"
      >
        <form onSubmit={handleWithdrawSubmit} className="space-y-4">
          <div className="bg-amber-50 border-l-4 border-amber-500 p-3 rounded text-[11px] text-amber-800 font-semibold">
            Warning: Withdrawing this package will invalidate the offer and candidates won't be able to respond to it.
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-700 mb-1">Reason for Withdrawal *</label>
            <textarea 
              required 
              rows={4}
              value={withdrawReason}
              onChange={(e) => setWithdrawReason(e.target.value)}
              placeholder="e.g. Budget constraints, profile reassessed, or candidate requested modifications..."
              className="w-full border rounded-lg p-2.5 text-xs outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          <button 
            type="submit" 
            disabled={withdrawing} 
            className="w-full bg-primary hover:bg-red-700 text-white py-2.5 rounded-lg text-xs font-bold disabled:opacity-50 transition-all flex items-center justify-center gap-1.5"
          >
            {withdrawing ? 'Withdrawing...' : 'Confirm Withdrawal'}
          </button>
        </form>
      </FormOffCanvas>

      {/* Reusable DeleteConfirmationOffcanvas Integration for standardizing Deletes */}
      <DeleteConfirmationOffcanvas
        isOpen={!!selectedOfferToDelete}
        onClose={() => setSelectedOfferToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Candidate Offer Record"
        itemName={selectedOfferToDelete ? `${selectedOfferToDelete.candidateId?.firstName}'s Offer Package (${selectedOfferToDelete.offerId || 'PENDING ID'})` : ''}
        isLoading={isDeleting}
      />

      {/* 👈 Reusable DeleteConfirmationOffcanvas dynamically mapped for verifying Official Issue triggers */}
      <DeleteConfirmationOffcanvas
        isOpen={!!selectedOfferToIssue}
        onClose={() => setSelectedOfferToIssue(null)}
        onConfirm={handleIssueConfirm}
        title="Confirm Issuing Offer"
        confirmButtonText="Issue Offer"
        isLoading={isIssuing}
        customDescription={
          selectedOfferToIssue ? (
            <span>
              Are you sure you want to officially issue and send the job offer letter package to{" "}
              <span className="font-semibold text-gray-800">
                {selectedOfferToIssue.candidateId?.firstName} {selectedOfferToIssue.candidateId?.lastName}
              </span>? This will notify them via email.
            </span>
          ) : null
        }
      />
    </>
  );
};

export default OfferList;